import { useEffect, useMemo, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "./ui/sheet";
import { api, INR, notifyApiError } from "../lib/api";
import { toast } from "sonner";
import { Crown, User, CheckCircle2 } from "lucide-react";

// "18 Jul" — date only, no time.
function formatDateOnly(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
  });
}

// "2:00 PM" — 12-hour time only, no date.
function formatTimeOnly(d) {
  if (!d) return "—";
  return new Date(d).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

// "Cash" / "UPI" / "Card" from the raw payment_mode value.
function formatPaymentMode(mode) {
  if (!mode) return "";
  if (mode.toLowerCase() === "upi") return "UPI";
  return mode.charAt(0).toUpperCase() + mode.slice(1).toLowerCase();
}

export default function HistoryDrawer({ open, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  // Table filter — "" means "All tables".
  const [selectedTableId, setSelectedTableId] = useState("");
  // Payment status filter: "all" | "paid" | "unpaid"
  const [statusFilter, setStatusFilter] = useState("all");
  // Per-row state when marking paid from the drawer
  const [savingId, setSavingId] = useState(null);
  const [modeById, setModeById] = useState({});

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/sessions?limit=500");
        setItems(data);
      } catch (e) {
        notifyApiError(toast, e);
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  // Unique tables that appear in the loaded history, used to populate the filter.
  const tableOptions = useMemo(() => {
    const map = new Map();
    for (const s of items) {
      if (!s.table_id) continue;
      const prev = map.get(s.table_id) || { id: s.table_id, name: s.table_name || "—", count: 0 };
      prev.count += 1;
      // Always keep the latest non-empty name we saw
      if (s.table_name) prev.name = s.table_name;
      map.set(s.table_id, prev);
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  const filteredItems = useMemo(() => {
    let list = items;
    if (selectedTableId) list = list.filter((s) => s.table_id === selectedTableId);
    if (statusFilter === "paid") {
      list = list.filter((s) => s.status === "completed" && s.payment_status === "paid");
    } else if (statusFilter === "unpaid") {
      list = list.filter((s) => s.status === "completed" && s.payment_status !== "paid");
    }
    return list;
  }, [items, selectedTableId, statusFilter]);

  // Counts for the status tabs — respect the table filter only.
  const tableFiltered = useMemo(() => {
    if (!selectedTableId) return items;
    return items.filter((s) => s.table_id === selectedTableId);
  }, [items, selectedTableId]);
  const counts = useMemo(() => {
    const completed = tableFiltered.filter((s) => s.status === "completed");
    const paid = completed.filter((s) => s.payment_status === "paid").length;
    const unpaid = completed.length - paid;
    return { all: tableFiltered.length, paid, unpaid };
  }, [tableFiltered]);

  const markPaid = async (s) => {
    const mode = modeById[s.id] || s.payment_mode || "cash";
    setSavingId(s.id);
    try {
      const { data } = await api.patch(`/sessions/${s.id}/payment`, {
        payment_status: "paid",
        payment_mode: mode,
      });
      setItems((prev) => prev.map((it) => (it.id === s.id ? { ...it, ...data } : it)));
      toast.success(`Marked paid — ${s.player_name} · ${INR(s.amount)}`);
    } catch (e) {
      notifyApiError(toast, e);
    } finally {
      setSavingId(null);
    }
  };

  const selectedTableName = useMemo(() => {
    if (!selectedTableId) return "All tables";
    return tableOptions.find((t) => t.id === selectedTableId)?.name || "—";
  }, [selectedTableId, tableOptions]);

  // Today's totals respect the current table filter.
  const today = new Date().toDateString();
  const totalToday = filteredItems
    .filter(
      (s) =>
        s.status === "completed" &&
        s.payment_status === "paid" &&
        new Date(s.end_time || s.start_time).toDateString() === today
    )
    .reduce((acc, s) => acc + (s.amount || 0), 0);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        className="bg-[#0a0f0d] text-white border-white/10 w-full sm:max-w-md overflow-y-auto"
        data-testid="history-drawer"
      >
        <SheetHeader>
          <SheetTitle className="text-white text-2xl font-black">
            Session History
          </SheetTitle>
          <SheetDescription className="text-gray-400">
            Today&apos;s revenue ({selectedTableName}):{" "}
            <span className="text-amber-300 font-bold font-mono">
              {INR(totalToday)}
            </span>
          </SheetDescription>
        </SheetHeader>

        {/* Table filter */}
        <div className="mt-5 space-y-1.5" data-testid="history-filter-bar">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-[0.25em] text-gray-400 font-bold">
              Table
            </span>
            {selectedTableId && (
              <button
                type="button"
                onClick={() => setSelectedTableId("")}
                data-testid="history-clear-filter"
                className="text-[10px] uppercase tracking-widest font-bold text-amber-300 hover:text-amber-200"
              >
                Clear filter
              </button>
            )}
          </div>
          <select
            data-testid="history-table-filter"
            value={selectedTableId}
            onChange={(e) => setSelectedTableId(e.target.value)}
            className="w-full bg-black/50 border border-white/10 text-white text-sm rounded-md px-3 py-2 outline-none focus:border-amber-400/60 focus:ring-2 focus:ring-amber-400/30 cursor-pointer"
          >
            <option value="">All tables · {items.length} session{items.length === 1 ? "" : "s"}</option>
            {tableOptions.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} · {t.count} session{t.count === 1 ? "" : "s"}
              </option>
            ))}
          </select>
        </div>

        {/* Payment-status pills — All / Paid / Unpaid */}
        <div
          className="mt-3 grid grid-cols-3 gap-1.5 p-1 bg-black/40 border border-white/10 rounded-lg"
          data-testid="history-status-tabs"
        >
          {[
            { key: "all", label: "All", count: counts.all, accent: "text-white" },
            { key: "paid", label: "Paid", count: counts.paid, accent: "text-emerald-300" },
            { key: "unpaid", label: "Unpaid", count: counts.unpaid, accent: "text-rose-300" },
          ].map((t) => {
            const active = statusFilter === t.key;
            return (
              <button
                key={t.key}
                type="button"
                data-testid={`history-tab-${t.key}`}
                onClick={() => setStatusFilter(t.key)}
                className={`flex items-center justify-center gap-1.5 py-1.5 rounded-md text-[11px] uppercase tracking-widest font-bold transition ${
                  active
                    ? "bg-amber-400/15 text-amber-200 ring-1 ring-amber-400/30"
                    : `${t.accent}/70 hover:bg-white/5`
                }`}
              >
                {t.label}
                <span
                  className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                    active ? "bg-amber-400/25 text-amber-100" : "bg-white/10 text-gray-300"
                  }`}
                >
                  {t.count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-4 space-y-3">
          {loading && (
            <div className="text-gray-500 text-sm text-center py-10">
              Loading…
            </div>
          )}
          {!loading && filteredItems.length === 0 && (
            <div className="text-gray-500 text-sm text-center py-10">
              {selectedTableId
                ? `No sessions for ${selectedTableName} yet.`
                : "No sessions yet."}
            </div>
          )}
          {filteredItems.map((s) => (
            <div
              key={s.id}
              data-testid={`history-item-${s.id}`}
              className="bg-[#121a16] border border-white/10 rounded-xl p-3 flex items-start justify-between gap-3"
            >
              <div className="min-w-0 flex-1">
                {/* Line 1: Player name */}
                <div className="flex items-center gap-1.5">
                  {s.player_type === "member" || s.player_type === "cross_club_member" ? (
                    <Crown className="w-3.5 h-3.5 text-amber-300 shrink-0" />
                  ) : (
                    <User className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  )}
                  <span
                    data-testid={`history-item-${s.id}-player`}
                    className="font-bold text-white truncate"
                  >
                    {s.player_name}
                  </span>
                </div>

                {/* Line 2: Table number/name */}
                <div
                  data-testid={`history-item-${s.id}-table`}
                  className="text-[11px] uppercase tracking-widest text-gray-500 font-bold mt-0.5"
                >
                  {s.table_name || "—"}
                </div>

                {/* Line 3: date, start time – end time · total */}
                <div
                  data-testid={`history-item-${s.id}-datetime`}
                  className="text-xs text-gray-400 font-mono mt-1"
                >
                  {formatDateOnly(s.start_time)}, {formatTimeOnly(s.start_time)}
                  {" – "}
                  {s.end_time ? formatTimeOnly(s.end_time) : "Running"}
                  {s.duration_minutes != null && (
                    <span className="text-gray-500">
                      {" "}
                      ({s.duration_minutes} min)
                    </span>
                  )}
                  {s.amount != null && (
                    <>
                      {" · "}
                      <span className="text-amber-300 font-bold tabular-nums">
                        {INR(s.amount)}
                      </span>
                    </>
                  )}
                </div>

                {/* Line 4: payment status (Paid · Cash/UPI/Card, or Unpaid/Running) */}
                <div className="mt-1.5 flex gap-1.5 items-center flex-wrap">
                  <span
                    data-testid={`history-item-${s.id}-status`}
                    className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold ${
                      s.status === "running"
                        ? "bg-red-500/20 text-red-300"
                        : s.payment_status === "paid"
                          ? "bg-emerald-500/20 text-emerald-300"
                          : "bg-amber-500/20 text-amber-300"
                    }`}
                  >
                    {s.status === "running"
                      ? "Running"
                      : s.payment_status === "paid"
                        ? "Paid"
                        : s.payment_status || "Unpaid"}
                    {s.payment_status === "paid" && s.payment_mode
                      ? ` · ${formatPaymentMode(s.payment_mode)}`
                      : ""}
                  </span>
                </div>
              </div>
              <div className="text-right shrink-0 flex flex-col items-end gap-1.5">
                {s.status === "completed" && s.payment_status !== "paid" && (
                  <div className="flex items-center gap-1.5">
                    <select
                      data-testid={`history-mode-${s.id}`}
                      value={modeById[s.id] || s.payment_mode || "cash"}
                      onChange={(e) =>
                        setModeById((m) => ({ ...m, [s.id]: e.target.value }))
                      }
                      disabled={savingId === s.id}
                      className="bg-black/50 border border-white/10 text-gray-200 text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 outline-none focus:border-amber-400/60 cursor-pointer"
                    >
                      <option value="cash">Cash</option>
                      <option value="upi">UPI</option>
                      <option value="card">Card</option>
                    </select>
                    <button
                      type="button"
                      data-testid={`history-mark-paid-${s.id}`}
                      onClick={() => markPaid(s)}
                      disabled={savingId === s.id}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/90 hover:bg-emerald-400 disabled:opacity-50 text-black text-[10px] font-bold uppercase tracking-wider transition"
                    >
                      <CheckCircle2 className="w-3 h-3" />
                      {savingId === s.id ? "…" : "Paid"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Footer totals for the current filter */}
        {filteredItems.length > 0 && (
          <div className="mt-4 pt-3 border-t border-white/5 text-xs flex items-center justify-between">
            <span className="text-gray-500 uppercase tracking-widest font-bold">
              {selectedTableName} · {filteredItems.length} session{filteredItems.length === 1 ? "" : "s"}
            </span>
            <span>
              <span className="text-gray-500 uppercase tracking-widest mr-2">Total collected</span>
              <span className="text-amber-300 font-mono font-bold tabular-nums">
                {INR(
                  filteredItems
                    .filter((s) => s.status === "completed" && s.payment_status === "paid")
                    .reduce((acc, s) => acc + (s.amount || 0), 0)
                )}
              </span>
            </span>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
